import React, { FunctionComponent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Auth } from 'aws-amplify';
import { Redirect, NavLink } from 'react-router-dom';
import withStyles from 'react-jss';
import { Button, Header, Form, Message, Card } from 'semantic-ui-react';
import EmailInput from 'components/FormComponents/EmailInput';
import { sanitizeMessage } from 'components/UserMgmt/UserMgmt.helpers';

import Loading from 'components/Loading';
import { ClassProp } from 'helpers/types';

import styles from 'components/UserMgmt/UserMgmt.styles';

export type Values = {
  email: string;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

type ForgotPasswordForm = {
  email: string;
};

type Status = {
  submitting: boolean;
  errors?: string;
  success: boolean;
};

const initialStatus: Status = {
  submitting: false,
  errors: undefined,
  success: false,
};

const ForgotPassword: FunctionComponent<Props> = ({ classes }) => {
  const [status, setStatus] = useState<Status>(initialStatus);

  const methods = useForm<ForgotPasswordForm>({
    mode: 'onBlur',
  });

  const { handleSubmit, trigger, formState } = methods;

  const onSubmit = async ({ email }: ForgotPasswordForm) => {
    setStatus({ ...status, submitting: true });
    try {
      await Auth.forgotPassword(email);
      setStatus({ submitting: false, success: true, errors: undefined });
    } catch (error) {
      const errorMsg = sanitizeMessage(error, 'Email sent!');
      setStatus({ submitting: false, success: false, errors: errorMsg });
    }
  };

  const { submitting, errors, success } = status;

  if (success) {
    return <Redirect to="/new-password" />;
  }

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      trigger('email');
    }
  };

  return (
    <>
      <Header className={classes.header} as="h1">
        Forgot Password
      </Header>
      <Card className={classes.container} fluid centered>
        <Card.Content>
          {submitting && <Loading loading />}
          <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(onSubmit)} error={!!errors}>
              {errors && !success && <Message error content={errors} />}

              <Form.Field className={classes.field}>
                <EmailInput
                  label="Email"
                  name="email"
                  placeholder="Enter your Email"
                  autoComplete="username"
                  onKeyDown={onKeyDownHandler}
                />
              </Form.Field>
              <Button
                as={NavLink}
                to={'/login'}
                floated={'right'}
                className={classes.backNavLink}
              >
                Return to Login
              </Button>

              <Form.Field>
                <Button
                  fluid
                  type="submit"
                  className={classes.button}
                  disabled={!formState.isValid || submitting}
                >
                  Get Code
                </Button>
              </Form.Field>
            </Form>
          </FormProvider>
        </Card.Content>
      </Card>
    </>
  );
};

export default withStyles(styles)(ForgotPassword);
